/**
 * LogMovementModal — step-driven money-movement entry.
 *
 * The investor sees their subscribed loop's steps as the primary choice:
 *   1. Pick a step (e.g. "Step 1: Taekus Business → EasyBitcoin @ 2.2%")
 *   2. Pick which family member moved the money
 *   3. Confirm amount (pre-filled from steps.amount) + date
 *
 * Behind the scenes we ensure financial_accounts rows exist for the
 * (member, source_kind) and (member, dest_kind) pairs — auto-creating
 * them with institution=kind label on first use, so the investor never
 * has to do a "set up accounts" dance to log their first movement.
 *
 * Falls back to a free-form mode when:
 *   - the holder isn't subscribed to any loop yet, OR
 *   - the user explicitly switches to "Off-loop movement".
 *
 * On submit: inserts one money_movements row. The DB trigger does the
 * step matching + value-event computation. We dispatch
 * finops:enrollment-changed so Home re-fetches.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { getSupabase } from '../lib/supabase.js';
import { useInvestmentData } from '../lib/useInvestmentData.js';
import { defaultOwnerFor, ownerOptionLabel } from '../lib/accountOwner.js';

// Contributions into the Kareem-controlled pool funding accounts are logged
// PENDING and become available only when Kareem confirms them (D22 §2
// addendum, 2026-08-04; enforced DB-side by guard_pool_confirm). The gold
// bridge kind is deliberately absent: bridge movements are admin-created
// already-confirmed at gold settlement time — the cash is already in TK-C.
export const POOL_FUNDING_DEST_KINDS = ['kareem_trust', 'kareem_kraken'];

const modalStyle = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 20, zIndex: 9999,
};
const panelStyle = {
  background: 'var(--surface, #fff)', color: 'var(--text-1, #0f172a)',
  borderRadius: 'var(--radius-lg, 16px)', padding: '24px 24px 18px',
  maxWidth: 560, width: '100%', maxHeight: '90vh', overflowY: 'auto',
  boxShadow: '0 20px 60px rgba(15,23,42,0.3)',
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
};
const labelStyle = {
  display: 'block', fontSize: 10.5, fontWeight: 800,
  color: 'var(--text-3, #94a3b8)', marginBottom: 6,
  textTransform: 'uppercase', letterSpacing: '0.08em',
};
const inputStyle = {
  width: '100%', padding: '10px 12px',
  border: '1px solid var(--border-2, #d4d8e6)', borderRadius: 8,
  fontSize: 14, background: 'var(--surface, #fff)',
  color: 'var(--text-1, #0f172a)',
  boxSizing: 'border-box', fontFamily: 'inherit',
};
const fieldStyle = { marginBottom: 14 };
const errorStyle = {
  background: 'var(--rose-bg, #fef2f2)', border: '1px solid var(--rose-border, #fecaca)',
  color: 'var(--rose, #e11d48)', padding: '10px 12px', borderRadius: 8,
  fontSize: 13, marginBottom: 12,
};

const fmt$ = (n) => '$' + Math.round(Math.abs(n)).toLocaleString();
// Ensure a financial_accounts row exists for (memberId, kind). Creates
// one on first use with institution = kindLabel so the user never sees
// a "set up accounts first" gate. Exported for PendingDrafts' draw
// re-pointing (T-009 Build 1): changing a pending draw's suggestion needs
// the same (member, kind) account guarantee before the dest swap.
export async function ensureAccount(sb, memberId, kind, kindLabel) {
  if (!sb || !memberId || !kind) throw new Error('Missing member or kind');
  const { data: existing, error: lookupErr } = await sb.from('financial_accounts')
    .select('id').eq('member_id', memberId).eq('account_kind', kind).eq('active', true)
    .order('created_at', { ascending: true }).limit(1);
  if (lookupErr) throw lookupErr;
  if (existing && existing[0]) return existing[0].id;
  const { data: created, error: createErr } = await sb.from('financial_accounts')
    .insert({ member_id: memberId, account_kind: kind, institution: kindLabel || kind, active: true })
    .select('id').single();
  if (createErr) throw createErr;
  return created.id;
}

export default function LogMovementModal({ member, profile, presetLoop, onClose }) {
  const sb = getSupabase();
  const { data: invData, status } = useInvestmentData(member, profile);

  // Loops the holder is subscribed to. If parent passed a presetLoop
  // (clicked "+ Log movement" on a specific loop card), default to that
  // one. Otherwise the user picks from the dropdown below.
  const allLoops = invData?.loops || (invData?.loop ? [invData.loop] : []);
  const [pickedLoopId, setPickedLoopId] = useState(presetLoop?.id || allLoops[0]?.id || '');
  useEffect(() => {
    if (presetLoop?.id) setPickedLoopId(presetLoop.id);
    else if (!pickedLoopId && allLoops[0]) setPickedLoopId(allLoops[0].id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetLoop?.id, allLoops.length]);
  const loop = allLoops.find(l => l.id === pickedLoopId) || presetLoop || allLoops[0] || null;
  const steps = loop?.steps || [];
  const family = invData?.members || [];
  const householdAccounts = invData?.accounts || [];

  // Default to step-driven mode if subscribed; off-loop mode otherwise.
  const [mode, setMode] = useState(steps.length > 0 ? 'step' : 'offloop');
  useEffect(() => {
    // When data finishes loading, pick the saner default.
    if (status !== 'loading' && status !== 'error') {
      setMode(steps.length > 0 ? 'step' : 'offloop');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, steps.length]);

  // Step-mode state
  const [stepIdx, setStepIdx] = useState(0);
  const [whoId, setWhoId] = useState(member?.id || '');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  // Default the amount to the step's catalog amount whenever the picked
  // step changes (and only if the user hasn't typed something custom yet).
  const pickedStep = steps[stepIdx] || null;
  useEffect(() => {
    if (pickedStep && pickedStep.amount != null && amount === '') {
      setAmount(String(Math.round(pickedStep.amount)));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedStep?.id]);

  // Whose-money picker: holder + sub-accounts.
  useEffect(() => {
    if (!whoId && family.length) setWhoId(family[0].id);
  }, [family, whoId]);

  const projectedValue = useMemo(() => {
    const amt = Number(amount);
    if (!pickedStep || !isFinite(amt) || amt === 0) return null;
    return Math.round(amt * (pickedStep.rate || 0));
  }, [amount, pickedStep]);

  // Off-loop mode state
  const [ofSourceKind, setOfSourceKind] = useState('');
  const [ofDestKind, setOfDestKind] = useState('');
  const [ofKinds, setOfKinds] = useState([]); // [{name,label}]

  // T-047: per-side account owners. Re-defaulted whenever the member, the
  // step, or the off-loop kinds change; the investor can override either
  // side from the pickers below.
  const activeSrcKind = mode === 'step' ? (pickedStep?.sourceKind || '') : ofSourceKind;
  const activeDstKind = mode === 'step' ? (pickedStep?.destKind || '') : ofDestKind;
  const activeSrcLabel = mode === 'step'
    ? (pickedStep?.source || '')
    : ((ofKinds.find((k) => k.name === ofSourceKind)?.label) || ofSourceKind.replace(/_/g, ' '));
  const activeDstLabel = mode === 'step'
    ? (pickedStep?.dest || '')
    : ((ofKinds.find((k) => k.name === ofDestKind)?.label) || ofDestKind.replace(/_/g, ' '));
  const [srcOwnerId, setSrcOwnerId] = useState('');
  const [dstOwnerId, setDstOwnerId] = useState('');
  useEffect(() => {
    setSrcOwnerId(defaultOwnerFor(activeSrcKind, whoId, householdAccounts));
    setDstOwnerId(defaultOwnerFor(activeDstKind, whoId, householdAccounts));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whoId, activeSrcKind, activeDstKind, householdAccounts.length]);
  const memberName = (id) => {
    const m = family.find((x) => x.id === id);
    return m ? (m.role === 'holder' ? `${m.name} (you)` : m.name) : '';
  };
  useEffect(() => {
    if (mode !== 'offloop' || !sb) return;
    (async () => {
      const { data } = await sb.from('inv_account_kinds').select('name, label').order('sort_order', { ascending: true });
      const list = data || [];
      setOfKinds(list);
      if (!ofSourceKind && list[0]) setOfSourceKind(list[0].name);
      if (!ofDestKind && list[1]) setOfDestKind(list[1].name);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const submitStepMovement = async () => {
    setErr(null);
    if (!pickedStep) { setErr('Pick a step first.'); return; }
    if (!whoId) { setErr('Pick which family member moved the money.'); return; }
    const amt = Number(amount);
    if (!isFinite(amt) || amt === 0) { setErr('Enter a non-zero amount.'); return; }
    setSaving(true);
    try {
      const srcId = await ensureAccount(sb, srcOwnerId || whoId, pickedStep.sourceKind, pickedStep.source);
      const dstId = await ensureAccount(sb, dstOwnerId || whoId, pickedStep.destKind, pickedStep.dest);
      if (srcId === dstId) throw new Error('Source and destination resolve to the same account.');
      const payload = {
        source_account_id: srcId,
        dest_account_id: dstId,
        amount: amt,
        movement_date: date,
        notes: notes || `Cycle step ${pickedStep.label || pickedStep.idx}: ${pickedStep.source} → ${pickedStep.dest}`,
        source: 'manual',
        funded_by_member_id: whoId,
      };
      // D22 §2 addendum: a contribution into the Kareem-controlled pool
      // funding accounts is logged PENDING; it becomes available when
      // Kareem confirms it (a DB guard makes that flip admin-only).
      if (POOL_FUNDING_DEST_KINDS.includes(pickedStep.destKind)) payload.status = 'pending';
      const { error } = await sb.from('money_movements').insert(payload);
      if (error) throw error;
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('finops:enrollment-changed', { detail: { memberId: member?.id } }));
      }
      onClose();
    } catch (e) {
      setErr(e?.message || String(e));
      setSaving(false);
    }
  };

  const submitOffLoopMovement = async () => {
    setErr(null);
    if (!whoId) { setErr('Pick which family member moved the money.'); return; }
    if (!ofSourceKind || !ofDestKind) { setErr('Pick both a source and destination kind.'); return; }
    if (ofSourceKind === ofDestKind) { setErr('Source and destination must be different kinds.'); return; }
    const amt = Number(amount);
    if (!isFinite(amt) || amt === 0) { setErr('Enter a non-zero amount.'); return; }
    setSaving(true);
    try {
      const lbl = (slug) => (ofKinds.find(k => k.name === slug)?.label) || slug.replace(/_/g, ' ');
      const srcId = await ensureAccount(sb, srcOwnerId || whoId, ofSourceKind, lbl(ofSourceKind));
      const dstId = await ensureAccount(sb, dstOwnerId || whoId, ofDestKind, lbl(ofDestKind));
      if (srcId === dstId) throw new Error('Source and destination resolve to the same account.');
      const payload = {
        source_account_id: srcId,
        dest_account_id: dstId,
        amount: amt,
        movement_date: date,
        notes: notes || null,
        source: 'manual',
        funded_by_member_id: whoId,
      };
      // Same pending rule as step mode (D22 §2 addendum).
      if (POOL_FUNDING_DEST_KINDS.includes(ofDestKind)) payload.status = 'pending';
      const { error } = await sb.from('money_movements').insert(payload);
      if (error) throw error;
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('finops:enrollment-changed', { detail: { memberId: member?.id } }));
      }
      onClose();
    } catch (e) {
      setErr(e?.message || String(e));
      setSaving(false);
    }
  };

  const memberLabel = (m) => m.role === 'holder' ? `${m.name} (you)` : m.name;

  // Shown only when the household has more than one member and both kinds
  // are known. Reads back the resolved route in plain words so a cross-member
  // transfer is never a surprise.
  const ownerPickers = (family.length > 1 && activeSrcKind && activeDstKind) ? (
    <>
      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ ...fieldStyle, flex: 1 }}>
          <label style={labelStyle}>From whose {activeSrcLabel}</label>
          <select value={srcOwnerId} onChange={(e) => setSrcOwnerId(e.target.value)} style={inputStyle}>
            {family.map((m) => (
              <option key={m.id} value={m.id}>{ownerOptionLabel(m, activeSrcKind, activeSrcLabel, householdAccounts)}</option>
            ))}
          </select>
        </div>
        <div style={{ ...fieldStyle, flex: 1 }}>
          <label style={labelStyle}>To whose {activeDstLabel}</label>
          <select value={dstOwnerId} onChange={(e) => setDstOwnerId(e.target.value)} style={inputStyle}>
            {family.map((m) => (
              <option key={m.id} value={m.id}>{ownerOptionLabel(m, activeDstKind, activeDstLabel, householdAccounts)}</option>
            ))}
          </select>
        </div>
      </div>
      {srcOwnerId && dstOwnerId && (
        <div style={{ fontSize: 12, color: 'var(--text-2, #475569)', marginTop: -6, marginBottom: 14 }}>
          Money leaves <strong>{memberName(srcOwnerId)}'s {activeSrcLabel}</strong> and lands in <strong>{memberName(dstOwnerId)}'s {activeDstLabel}</strong>
          {(srcOwnerId !== whoId || dstOwnerId !== whoId) ? <> — counted for {memberName(whoId)}.</> : '.'}
        </div>
      )}
    </>
  ) : null;

  return (
    <div onClick={onClose} style={modalStyle}>
      <div onClick={(e) => e.stopPropagation()} style={panelStyle}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--accent, #4f46e5)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>
              Log a money movement
            </div>
            <h2 style={{ fontSize: 19, fontWeight: 800, margin: 0 }}>
              {mode === 'step' ? 'Which step did you just execute?' : 'Log an off-loop movement'}
            </h2>
            <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--text-2, #475569)' }}>
              {mode === 'step'
                ? <>One row per actual move of money. The matching trigger will fire a value event for matching, subscribed steps.</>
                : <>Use this when you moved money between kinds that aren't part of your subscribed loop.</>}
            </p>
          </div>
          <button onClick={onClose} aria-label="Close"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--text-3, #94a3b8)', lineHeight: 1, padding: 4, marginTop: -4 }}>
            ✕
          </button>
        </div>

        {/* Mode toggle — only when there's actually a loop to pick from */}
        {steps.length > 0 && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 16, background: 'var(--surface-2, #f3f4fa)', padding: 3, borderRadius: 10 }}>
            <button onClick={() => { setErr(null); setMode('step'); }}
              style={{
                flex: 1, padding: '7px 10px', border: 'none', borderRadius: 8,
                background: mode === 'step' ? 'var(--surface)' : 'transparent',
                color: mode === 'step' ? 'var(--text-1)' : 'var(--text-2)',
                boxShadow: mode === 'step' ? '0 1px 2px rgba(15,23,42,0.08)' : 'none',
                fontWeight: 700, fontSize: 12.5, cursor: 'pointer',
              }}>
              On-loop step
            </button>
            <button onClick={() => { setErr(null); setMode('offloop'); }}
              style={{
                flex: 1, padding: '7px 10px', border: 'none', borderRadius: 8,
                background: mode === 'offloop' ? 'var(--surface)' : 'transparent',
                color: mode === 'offloop' ? 'var(--text-1)' : 'var(--text-2)',
                boxShadow: mode === 'offloop' ? '0 1px 2px rgba(15,23,42,0.08)' : 'none',
                fontWeight: 700, fontSize: 12.5, cursor: 'pointer',
              }}>
              Off-loop
            </button>
          </div>
        )}

        {err && <div style={errorStyle}>{err}</div>}

        {status === 'loading' ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>Loading…</div>
        ) : mode === 'step' && steps.length === 0 ? (
          <div style={{ padding: '14px 16px', background: 'var(--accent-bg)', border: '1px solid var(--accent-soft)', color: 'var(--accent-2)', borderRadius: 8, fontSize: 13 }}>
            You're not subscribed to any loop yet. Browse the catalog to subscribe — then your movements can match the loop's steps.
          </div>
        ) : mode === 'step' ? (
          <>
            {/* Loop picker — only shown when the holder is subscribed to
                more than one loop AND no presetLoop was passed in by the
                parent. When the user clicked "+ Log movement" on a specific
                loop card, we lock to that loop (the modal title still names
                it so they know which one). The user can cancel and pick a
                different loop's card if they meant another loop. */}
            {allLoops.length > 1 && !presetLoop && (
              <div style={fieldStyle}>
                <label style={labelStyle}>Loop</label>
                <select value={pickedLoopId} onChange={(e) => { setPickedLoopId(e.target.value); setStepIdx(0); setAmount(''); }} style={inputStyle}>
                  {allLoops.map(lp => (
                    <option key={lp.id} value={lp.id}>{lp.name}{lp.paused ? ' (paused)' : ''}</option>
                  ))}
                </select>
              </div>
            )}
            <div style={fieldStyle}>
              <label style={labelStyle}>Which step{loop?.name ? ` in ${loop.name}` : ''}</label>
              <select value={stepIdx} onChange={(e) => { setStepIdx(parseInt(e.target.value, 10)); setAmount(''); }} style={inputStyle}>
                {steps.map((s, i) => (
                  <option key={s.id || i} value={i}>
                    Step {s.label || s.idx}: {s.source} → {s.dest} {s.rate ? `@ ${(s.rate * 100).toFixed(2).replace(/\.?0+$/, '')}%` : '@ 0%'}
                  </option>
                ))}
              </select>
            </div>

            {family.length > 1 && (
              <div style={fieldStyle}>
                <label style={labelStyle}>For which family member</label>
                <select value={whoId} onChange={(e) => setWhoId(e.target.value)} style={inputStyle}>
                  {family.map(m => <option key={m.id} value={m.id}>{memberLabel(m)}</option>)}
                </select>
              </div>
            )}
            {ownerPickers}

            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ ...fieldStyle, flex: 2 }}>
                <label style={labelStyle}>Amount ($)</label>
                <input
                  style={inputStyle}
                  onWheel={(e) => e.currentTarget.blur()} type="number" inputMode="decimal" step="0.01"
                  placeholder={pickedStep?.amount != null ? String(Math.round(pickedStep.amount)) : '5000'}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
                {pickedStep?.amount != null && Number(amount) !== Math.round(pickedStep.amount) && (
                  <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
                    Catalog default: {fmt$(pickedStep.amount)}
                    <button type="button" onClick={() => setAmount(String(Math.round(pickedStep.amount)))}
                      style={{ marginLeft: 6, background: 'transparent', border: 'none', color: 'var(--accent)', fontWeight: 700, cursor: 'pointer', padding: 0 }}>
                      use default
                    </button>
                  </div>
                )}
              </div>
              <div style={{ ...fieldStyle, flex: 1 }}>
                <label style={labelStyle}>Date</label>
                <input style={inputStyle} type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>Notes <span style={{ fontWeight: 400, color: 'var(--text-3)', textTransform: 'none', letterSpacing: 0 }}>· optional</span></label>
              <textarea
                style={{ ...inputStyle, minHeight: 56, resize: 'vertical' }}
                placeholder={pickedStep ? `Cycle step ${pickedStep.label || pickedStep.idx}: ${pickedStep.source} → ${pickedStep.dest}` : ''}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            {projectedValue != null && (
              <div style={{ padding: '10px 14px', background: 'var(--green-bg)', border: '1px solid var(--green-border)', borderRadius: 8, fontSize: 12.5, color: '#065f46', marginBottom: 12 }}>
                Projects a value event of <strong>{fmt$(projectedValue)}</strong> at this step's {(pickedStep.rate * 100).toFixed(2).replace(/\.?0+$/, '')}% rate.
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 4, justifyContent: 'flex-end' }}>
              <button onClick={onClose} disabled={saving}
                style={{ padding: '9px 16px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 8, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', color: 'var(--text-2)' }}>
                Cancel
              </button>
              <button onClick={submitStepMovement} disabled={saving || !pickedStep || !whoId || !amount}
                style={{ padding: '9px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Logging…' : amount ? `Log ${fmt$(Number(amount))} movement` : 'Log movement'}
              </button>
            </div>
          </>
        ) : (
          /* off-loop fallback */
          <>
            {family.length > 1 && (
              <div style={fieldStyle}>
                <label style={labelStyle}>For which family member</label>
                <select value={whoId} onChange={(e) => setWhoId(e.target.value)} style={inputStyle}>
                  {family.map(m => <option key={m.id} value={m.id}>{memberLabel(m)}</option>)}
                </select>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ ...fieldStyle, flex: 1 }}>
                <label style={labelStyle}>From kind</label>
                <select value={ofSourceKind} onChange={(e) => setOfSourceKind(e.target.value)} style={inputStyle}>
                  {ofKinds.map(k => <option key={k.name} value={k.name}>{k.label}</option>)}
                </select>
              </div>
              <div style={{ ...fieldStyle, flex: 1 }}>
                <label style={labelStyle}>To kind</label>
                <select value={ofDestKind} onChange={(e) => setOfDestKind(e.target.value)} style={inputStyle}>
                  {ofKinds.map(k => <option key={k.name} value={k.name}>{k.label}</option>)}
                </select>
              </div>
            </div>
            {ownerPickers}
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ ...fieldStyle, flex: 2 }}>
                <label style={labelStyle}>Amount ($)</label>
                <input style={inputStyle} onWheel={(e) => e.currentTarget.blur()} type="number" inputMode="decimal" step="0.01"
                  placeholder="5000" value={amount} onChange={(e) => setAmount(e.target.value)} />
              </div>
              <div style={{ ...fieldStyle, flex: 1 }}>
                <label style={labelStyle}>Date</label>
                <input style={inputStyle} type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Notes <span style={{ fontWeight: 400, color: 'var(--text-3)', textTransform: 'none', letterSpacing: 0 }}>· optional</span></label>
              <textarea style={{ ...inputStyle, minHeight: 56, resize: 'vertical' }}
                value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
            <div style={{ padding: '10px 12px', background: 'var(--surface-2)', borderRadius: 8, fontSize: 12, color: 'var(--text-2)', marginBottom: 12 }}>
              If this (from-kind → to-kind) transition isn't yet a step in the catalog, the matching engine will queue a draft for Kareem to set a rate.
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 4, justifyContent: 'flex-end' }}>
              <button onClick={onClose} disabled={saving}
                style={{ padding: '9px 16px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 8, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', color: 'var(--text-2)' }}>
                Cancel
              </button>
              <button onClick={submitOffLoopMovement} disabled={saving || !whoId || !amount}
                style={{ padding: '9px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Logging…' : amount ? `Log ${fmt$(Number(amount))} movement` : 'Log movement'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
